import React, { useMemo, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ArticleHeader from "./components/ArticleHeader";
import styles from "../../../styles/pages/articles/ArticleSingle.module.scss";
import coverSVG from "./coverSVG";
import { ArticleFiltersSidebar } from "./components/Sidebar";
import { useCreamTheme, useReadingProgress } from "./components/articleHooks";
import ArticleFooter from "./components/ArticleFooter";
import { articles, byId } from "../../../data/articlesIndex";

import useArticleTheme from "./components/useArticleTheme";


export default function ArticleSingle() {

  const { toggleTheme } = useArticleTheme();

  useCreamTheme();

  const navigate = useNavigate();

  // Route param is URL-encoded because ids contain "/"
  const { id } = useParams();
  const decodedId = decodeURIComponent(id || "");
  const meta = byId[decodedId];

  // reading progress
  const articleRef = useRef(null);
  const progress = useReadingProgress(articleRef);

  // sidebar options
  const categories = useMemo(
    () => ["all", ...Array.from(new Set((articles || []).map((a) => a.category).filter(Boolean)))],
    []
  );

  const topics = useMemo(
    () => ["all", ...Array.from(new Set((articles || []).map((a) => a.topic).filter(Boolean)))],
    []
  );

  const popularTags = useMemo(() => {
    const map = new Map();
    (articles || []).forEach((a) =>
      (a.tags || []).forEach((t) => map.set(t, (map.get(t) || 0) + 1))
    );
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([t]) => t);
  }, []);

  const goHomeWith = (next = {}) => {
    const sp = new URLSearchParams();
    if (next.category && next.category !== "all") sp.set("category", next.category);
    if (next.topic && next.topic !== "all") sp.set("topic", next.topic);
    if (next.tag) sp.set("tag", next.tag);
    if (next.featuredOnly) sp.set("featured", "1");
    const qs = sp.toString();
    navigate(`/article${qs ? `?${qs}` : ""}`);
  };

  if (!meta) {
    return (
      <main className={styles.container} style={{ padding: "3rem 0" }}>
        <h1 className={styles.heroSingleTitle}>Article not found</h1>
        <p className={styles.muted}>We couldn’t find “{decodedId || id}”.</p>
        <Link to="/article" className={styles.chip}>
          ← Back to Articles
        </Link>
      </main>
    );
  }

  const dateFmt = meta.date
    ? new Date(meta.date).toLocaleDateString(undefined, {
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
    : "";

  return (
    <>
      {/* reading progress */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: 3,
          width: `${progress}%`,
          background: "linear-gradient(90deg, var(--plum), var(--teal))",
          zIndex: 60,
        }}
        aria-hidden="true"
      />

      {/* header */}
     <ArticleHeader
      brand="NexHer"
      active="articles"
      showSearch={true}
      onToggleTheme={toggleTheme}
   />
      {/* hero */}
      <section className={styles.hero}>
        <div className={`${styles.container} ${styles.heroGrid}`}>
          <div>
            <span className={styles.eyebrow}>
              {(meta.category || "technology")[0].toUpperCase() +
                (meta.category || "technology").slice(1)}{" "}
              ·{" "}
              {(meta.topic || "general")[0].toUpperCase() +
                (meta.topic || "general").slice(1)}
            </span>

            <h1 className={styles.heroSingleTitle}>{meta.title}</h1>
            <p className={styles.heroSub}>{meta.summary}</p>

            <div
              style={{
                display: "flex",
                gap: ".7rem",
                alignItems: "center",
                marginTop: ".6rem",
                flexWrap: "wrap",
              }}
            >
              <span className={styles.avatar}>{(meta.author || "A")[0]}</span>
              {dateFmt ? <span>{dateFmt}</span> : null}
              <span>•</span>
              <span>{meta.read || 0} min</span>
              {(meta.tags || []).slice(0, 4).map((t) => (
                <span key={t} className={styles.tag}>
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className={styles.heroArt} aria-hidden="true">
              {meta.heroImage ? (
                <img
                  src={meta.heroImage}
                  alt={meta.heroAlt || meta.title}
                  loading="eager"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <img
                  src={coverSVG(meta.category, meta.topic)}
                  alt={`${meta.title} cover art`}
                  loading="eager"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
           </div>
        </div>
      </section>

      {/* layout */}
      <main className={`${styles.container} ${styles.layout}`} style={{ paddingBottom: "1rem" }}>
        {/* article content */}
        <article ref={articleRef} id="article" className={styles.prose}>
          {!meta.body ? (
            <p className={styles.muted}>
              No <code>body</code> found for this post yet. Run the generator.
            </p>
          ) : (
            // <ReactMarkdown remarkPlugins={[remarkGfm]}>{meta.body}</ReactMarkdown>
            <ReactMarkdown
  remarkPlugins={[remarkGfm]}
  components={{
    img: (props) => {
      const { title, ...rest } = props;

      return (
        <figure className={styles.figure}>
          <div className={styles.mediaBox}>
            <img
              {...rest}
              title={title}
              loading="lazy"
              decoding="async"
              className={styles.proseImg}
              onLoad={(e) => {
                const img = e.currentTarget;
                const box = img.closest(`.${styles.mediaBox}`);
                if (!box) return;

                const isPortrait = img.naturalHeight / img.naturalWidth > 1.1;
                box.classList.toggle(styles.portraitBox, isPortrait);
                box.classList.toggle(styles.landscapeBox, !isPortrait);
              }}
            />
          </div>

          {title ? <figcaption className={styles.figcaption}>{title}</figcaption> : null}
        </figure>
      );
    },
  }}
>
  {meta.body}
</ReactMarkdown>

          )}

          <div style={{ marginTop: "1.5rem" }}>
            <Link to="/article" className={styles.chip}>
              ← Back to Articles
            </Link>
          </div>
        </article>

        {/* sidebar */}
        <ArticleFiltersSidebar
          categories={categories}
          topics={topics}
          popularTags={popularTags}
          selectedCategory={meta.category}
          selectedTopic={meta.topic}
          selectedTag={null}
          featuredOnly={false}
          onSelectCategory={(c) => goHomeWith({ category: c })}
          onSelectTopic={(t) => goHomeWith({ topic: t })}
          onToggleTag={(t) => goHomeWith({ tag: t })}
          onToggleFeatured={() => goHomeWith({ featuredOnly: true })}
          onClear={() => navigate("/article")}
        />
      </main>

      {/* footer */}
     < ArticleFooter />
    </>
  );
}
